import {BehaviorSubject, Observable} from "rxjs/Rx";
import { debounce } from "lodash";

export interface IAndroidHandlerState {
  isAndroid: boolean;
  keyboardIsOpen: boolean;
  screenIsLandscape: boolean;
  windowHeight: number;
  windowWidth: number;
  currentHeight: number;
  currentWidth: number;
}

export class AndroidHandler {
  public readonly androidState$$: BehaviorSubject<IAndroidHandlerState>;
  public readonly keyboardIsOpen$$: Observable<boolean>;
  public readonly isAndroid$$: Observable<boolean>;

  private isAndroid: boolean;
  private keyboardIsOpen: boolean;
  private screenIsLandscape: boolean;
  private windowHeight: number;
  private windowWidth: number;
  private currentHeight: number;
  private currentWidth: number;

  constructor() {

    this.isAndroid = false;
    this.keyboardIsOpen = false;
    this.screenIsLandscape = false;
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;
    this.currentHeight = window.innerHeight;
    this.currentWidth = window.innerWidth;

    this.androidState$$ = new BehaviorSubject<IAndroidHandlerState>(this.getCurrentState());
    this.keyboardIsOpen$$ = this.androidState$$.map((state: IAndroidHandlerState) => state.keyboardIsOpen);
    this.isAndroid$$ = this.androidState$$.map((state: IAndroidHandlerState) => state.isAndroid);

    const checkAndroidChanges = () => this.checkAndroidChanges();
    checkAndroidChanges(); // first init
    // added debounced to limit the times we call this function when resizing. we need leading in order to keep the
    // original size when the keyboard start appearing and the trailing for when it finishes
    const debouncedCheckAndroid = debounce(checkAndroidChanges, 300, {leading: true, trailing: true});

    // @ts-ignore
    document.getElementsByTagName("BODY")[0].onresize = debouncedCheckAndroid;
  }

  private checkAndroidChanges() {
    // if we updated the state we need to update the stream as well
    // this is needed because we need to avoid to update streams several times in a second.
    this.updateAndroid();
    this.checkHeight();
    this.updateStream();
  }

  /**
   *
   * @private returns true if the state has been updated, false otherwise
   */
  private updateAndroid() {
    // in theory this should be fixed, but we will handle it in devtools too for testing.
    const isAndroid = !!navigator.userAgent.match(/Android/i);

    if (this.isAndroid !== isAndroid) {
      this.isAndroid = isAndroid;
    }
  }

  /**
   * check height and width and update the state if needed.
   *
   * changes in height and width if we are in android will determine if the keyboard is on or not. also it will
   * show if the webapp is in landscape mode
   *
   * @private returns true if the state has been updated, false otherwise
   */
  private checkHeight() {
    const newHeight = window.innerHeight;
    const newWidth = window.innerWidth;

    // change to landscape mode - testing
    if (this.windowWidth !== newWidth) {
      this.screenIsLandscape = newHeight < newWidth;
      this.windowHeight = newHeight;
      this.windowWidth = newWidth;
      this.updateCssHeightValue();

      // keyboard isOpen in android
    } else {
      this.keyboardIsOpen = this.windowHeight > newHeight && this.isAndroid;
      // if keyboard is open we need to force the height to the old one!
      // we wont save the new sizes because it will revert eventually.
      // but we must ensure that scroll is shown as well...
      if (!this.keyboardIsOpen) {
        this.windowHeight = newHeight;
        this.windowWidth = newWidth;
        this.updateCssHeightValue();
      }
    }

    this.currentHeight = newHeight;
    this.currentWidth = newWidth;
  }

  private updateStream() {
    this.androidState$$.next(this.getCurrentState());
  }

  private getCurrentState(): IAndroidHandlerState {
    return {
      isAndroid: this.isAndroid,
      keyboardIsOpen: this.keyboardIsOpen,
      screenIsLandscape: this.screenIsLandscape,
      windowHeight: this.windowHeight,
      windowWidth: this.windowWidth,
      currentHeight: this.currentHeight,
      currentWidth: this.currentWidth,
    };
  }

  private updateCssHeightValue() {
    const vh = this.windowHeight * 0.01;

    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
}
